import { CorpusId } from '../models/aliases';
import { events } from '../analytics/events';
import { FeedbackPopover } from './paper/FeedbackPopover';
import { Reaction } from '../models/reactions';
import { useTrackedHandler } from '../hooks/useTracking';

import { ComponentProps, useState } from 'react';
import { IconButton } from '@mui/material';
import ModeCommentOutlinedIcon from '@mui/icons-material/ModeCommentOutlined';
import styled from 'styled-components';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';

interface PaperFeedbackProps {
  corpusId: CorpusId;
  reaction: Reaction | undefined;
  onReactionChange?: (corpusId: CorpusId, status: Reaction | undefined) => void;
  onTextFeedbackSubmitted?: (corpusId: CorpusId, text: string) => void;
}

export function PaperReactionControls(props: PaperFeedbackProps): JSX.Element {
  const {
    corpusId,
    reaction,
    onReactionChange = () => {},
    onTextFeedbackSubmitted = () => {},
  } = props;

  const [isFeedbackFormOpen, setIsFeedbackFormOpen] = useState(false);
  const [feedbackFormAnchorEl, setFeedbackFormAnchorEl] =
    useState<Element | null>(null);

  const handleClickThumbsDown = useTrackedHandler(
    events.feedback.ThumbsDownClicked,
    () => {
      if (reaction === '-1') {
        onReactionChange(corpusId, undefined);
      } else {
        onReactionChange(corpusId, '-1');
      }
    },
    () => ({ corpusId, isSelected: reaction !== '-1' }),
  );
  const handleClickThumbsUp = useTrackedHandler(
    events.feedback.ThumbsUpClicked,
    () => {
      if (reaction === '+1') {
        onReactionChange(corpusId, undefined);
      } else {
        onReactionChange(corpusId, '+1');
      }
    },
    () => ({ corpusId, isSelected: reaction !== '+1' }),
  );

  const handleTextFeedbackClick = useTrackedHandler(
    events.feedback.TextFeedbackClicked,
    (event: React.MouseEvent<HTMLButtonElement>): void => {
      setFeedbackFormAnchorEl(event.currentTarget);
      setIsFeedbackFormOpen(true);
    },
    () => ({ corpusId }),
  );

  const handleCloseTextFeedbackPopover = useTrackedHandler(
    events.feedback.CloseTextFeedbackClicked,
    (): void => {
      setFeedbackFormAnchorEl(null);
      setIsFeedbackFormOpen(false);
    },
    () => ({ corpusId }),
  );

  const handleTextFeedbackSubmitted = useTrackedHandler(
    events.feedback.SubmitTextFeedbackClicked,
    async (text: string): Promise<void> => {
      setFeedbackFormAnchorEl(null);
      setIsFeedbackFormOpen(false);
      onTextFeedbackSubmitted(corpusId, text);
    },
    (text) => ({ corpusId, message: text }),
  );

  return (
    <>
      <ButtonRow>
        <ThumbsUpBtn
          isSelected={reaction === '+1'}
          onClick={handleClickThumbsUp}
        />
        <ThumbsDownBtn
          isSelected={reaction === '-1'}
          onClick={handleClickThumbsDown}
        />
        <FeedbackIconButton onClick={handleTextFeedbackClick}>
          <ModeCommentOutlinedIcon />
        </FeedbackIconButton>
      </ButtonRow>
      <FeedbackPopover
        corpusId={corpusId}
        onSubmitFeedback={handleTextFeedbackSubmitted}
        closeFeedbackForm={handleCloseTextFeedbackPopover}
        isFeedbackFormOpen={isFeedbackFormOpen}
        feedbackFormAnchorEl={feedbackFormAnchorEl}
      />
    </>
  );
}

const ButtonRow = styled.div`
  display: flex;
  height: 100%;
  align-items: center;
  margin-right: 6px;
`;

const ReactionBtn = styled.button`
  color: ${({ theme }) => theme.color.main.primary};
  background: none;
  border: none;
  cursor: pointer;
  padding: 1px 6px;

  & .MuiSvgIcon-root {
    width: 16px;
    height: 16px;
  }

  &:hover {
    opacity: 0.7;
  }

  &:active {
    opacity: 1;
  }
`;

const FeedbackIconButton = styled(IconButton)`
  padding: 1px 6px;

  & .MuiSvgIcon-root {
    width: 16px;
    height: 16px;
  }
`;

type ReactionBtnProps = ComponentProps<typeof ReactionBtn> & {
  isSelected: boolean;
};

export function ThumbsUpBtn(props: ReactionBtnProps): JSX.Element {
  const { isSelected, ...rest } = props;
  return (
    <ReactionBtn {...rest}>
      {isSelected ? <ThumbUpIcon /> : <ThumbUpOutlinedIcon />}
    </ReactionBtn>
  );
}

export function ThumbsDownBtn(props: ReactionBtnProps): JSX.Element {
  const { isSelected, ...rest } = props;
  return (
    <ReactionBtn {...rest}>
      {isSelected ? <ThumbDownIcon /> : <ThumbDownOutlinedIcon />}
    </ReactionBtn>
  );
}
